import { useState } from "react";
import { Lock, LogOut, ShieldCheck, Eye } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, errMsg, istDateTime } from "@/lib/api";

const Field = ({ id, label, ...p }) => (
  <label className="block text-xs text-slate-400">{label}
    <Input data-testid={id} {...p} className="mt-1 h-10 bg-slate-900 text-sm text-slate-100" />
  </label>
);

const Enroll = ({ s }) => (
  <div className="space-y-2 text-xs text-slate-400" data-testid="login-enroll">
    <p>First login: scan this with Google Authenticator (or any TOTP app), then enter the 6-digit code.</p>
    <img src={s.qr} alt="2FA QR code" className="mx-auto h-44 w-44 rounded bg-white p-2" data-testid="login-qr" />
    <p className="break-all text-center">Or type this key: <b className="num text-slate-200" data-testid="login-secret">{s.secret}</b></p>
  </div>
);

export const RecoveryCodes = ({ codes, onOk }) => (
  <div className="space-y-3" data-testid="recovery-codes">
    <p className="text-xs text-amber-400">Save these 8 recovery codes somewhere safe (print or password manager). Each one can replace the Authenticator code once if you lose your phone. They won't be shown again.</p>
    <div className="grid grid-cols-2 gap-2 rounded border border-slate-700 p-3">
      {codes.map((c) => <code key={c} className="num text-center text-sm text-slate-100" data-testid="recovery-code">{c}</code>)}
    </div>
    <div className="flex gap-2">
      <Button type="button" variant="outline" className="flex-1 border-slate-700 text-xs" data-testid="button-copy-recovery"
        onClick={() => navigator.clipboard?.writeText(codes.join("\n")).then(() => toast.success("Copied"))}>Copy</Button>
      <Button type="button" className="flex-1 text-xs" onClick={onOk} data-testid="button-recovery-saved">I've saved them</Button>
    </div>
  </div>
);

const Forgot = ({ onBack }) => {
  const [f, setF] = useState({ email: "", code: "", otp: "", new: "" });
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      if (!sent) { toast.success((await api.post("/auth/forgot", { email: f.email })).data.detail); setSent(true); }
      else { await api.post("/auth/reset", f); toast.success("Password changed. Sign in with the new password."); onBack(); }
    } catch (x) { setErr(errMsg(x)); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="panel w-full max-w-sm space-y-4 p-6" data-testid="forgot-form">
      <div className="text-lg font-semibold text-slate-100">Reset password</div>
      <Field id="forgot-email" label="Account email" type="email" value={f.email} onChange={set("email")} required disabled={sent} />
      {sent && (
        <>
          <Field id="forgot-code" label="6-digit code from the reset email" inputMode="numeric" maxLength={6} value={f.code} onChange={set("code")} required />
          <Field id="forgot-otp" label="Authenticator code, or a recovery code (ab12-cd34)" maxLength={12} value={f.otp} onChange={set("otp")} />
          <Field id="forgot-new" label="New password (10+ characters)" type="password" autoComplete="new-password" minLength={10} value={f.new} onChange={set("new")} required />
        </>
      )}
      {err && <p className="text-xs text-red-400" data-testid="forgot-error">{err}</p>}
      <Button type="submit" disabled={busy} className="h-10 w-full" data-testid="forgot-submit">{busy ? "Working…" : sent ? "Set new password" : "Email me a reset code"}</Button>
      <button type="button" className="w-full text-[11px] text-slate-500 hover:text-slate-300" onClick={onBack} data-testid="forgot-back">Back to sign in</button>
    </form>
  );
};

export const LoginPage = ({ onDone }) => {
  const [forgot, setForgot] = useState(false);
  const [codes, setCodes] = useState(null);
  const [email, setEmail] = useState("");
  const [pw, setPw] = useState("");
  const [code, setCode] = useState("");
  const [step, setStep] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      if (!step) { setStep((await api.post("/auth/login", { email, password: pw })).data); setCode(""); }
      else {
        const u = (await api.post("/auth/totp", { pending: step.pending, code: code.trim() })).data;
        if (u.recovery_codes) setCodes(u); else onDone(u);
      }
    } catch (x) {
      const m = errMsg(x);
      setErr(m);
      if (/expired\. Start again/.test(m)) setStep(null);
    } finally { setBusy(false); }
  };

  if (forgot) return <div className="flex min-h-screen items-center justify-center bg-[#080C14] p-4"><Forgot onBack={() => setForgot(false)} /></div>;
  if (codes) return <div className="flex min-h-screen items-center justify-center bg-[#080C14] p-4"><div className="panel w-full max-w-sm p-6"><RecoveryCodes codes={codes.recovery_codes} onOk={() => onDone(codes)} /></div></div>;
  return (
    <div className="flex min-h-screen items-center justify-center bg-[#080C14] p-4">
      <form onSubmit={submit} className="panel w-full max-w-sm space-y-4 p-6" data-testid="login-form">
        <div className="flex items-center gap-2 text-slate-100"><Lock size={16} /><span className="text-lg font-semibold">TheRealRupee · sign in</span></div>
        {!step ? (
          <>
            <Field id="login-email" label="Email" type="email" autoComplete="username" value={email} onChange={(e) => setEmail(e.target.value)} required />
            <Field id="login-password" label="Password" type="password" autoComplete="current-password" value={pw} onChange={(e) => setPw(e.target.value)} required />
          </>
        ) : (
          <>
            {step.step === "enroll" && <Enroll s={step} />}
            <Field id="login-code" label={step.step === "enroll" ? "6-digit code from your authenticator app" : "6-digit Authenticator code (or a recovery code like ab12-cd34)"}
              maxLength={12} autoFocus autoComplete="one-time-code" value={code} onChange={(e) => setCode(e.target.value)} required />
          </>
        )}
        {err && <p className="text-xs text-red-400" data-testid="login-error">{err}</p>}
        <Button type="submit" disabled={busy} className="h-10 w-full" data-testid="login-submit">{busy ? "Checking…" : step ? "Verify code" : "Continue"}</Button>
        {step && <button type="button" className="w-full text-[11px] text-slate-500 hover:text-slate-300" onClick={() => { setStep(null); setErr(""); }} data-testid="login-back">Back</button>}
        {!step && <button type="button" className="w-full text-[11px] text-indigo-300 hover:text-indigo-200" onClick={() => setForgot(true)} data-testid="link-forgot-password">Forgot password?</button>}
        <p className="text-[11px] text-slate-500">Sessions end every night at midnight IST. 5 wrong tries lock the account for 15 minutes.</p>
      </form>
    </div>
  );
};

export const UserBadge = ({ user, onLogout }) => (
  <div className="sticky right-0 ml-auto flex items-center gap-2 bg-[#0D121F] py-1.5 pl-3 text-[11px]" data-testid="user-badge">
    {user.role === "owner" ? <ShieldCheck size={13} className="text-emerald-400" /> : <Eye size={13} className="text-amber-400" />}
    <span className="hidden text-slate-400 sm:inline" title={`Session ends ${istDateTime(user.expires_at)}`}>{user.email}</span>
    {user.role !== "owner" && <span className="rounded bg-amber-500/15 px-1.5 py-0.5 font-semibold text-amber-400" data-testid="badge-read-only">READ-ONLY</span>}
    <Button size="sm" variant="ghost" onClick={() => { onLogout(); toast.success("Signed out"); }} className="h-7 gap-1 px-2 text-[11px]" data-testid="button-logout"><LogOut size={12} /> Sign out</Button>
  </div>
);
